import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import afroYapLogo from '../assets/afroyap_full_logo.png';
import { ParallaxBackground } from './utils/parallax';
import { useSettings } from './SettingsContext';
import { useProgressCounter } from '../hooks/useProgressCounter';

/**
 * The app's one loading visual, in two sizes.
 *
 * `boot` covers the cold start: opaque ground, larger logo, and a reload escape
 * hatch if the boot never finishes. `transition` covers screen-to-screen
 * navigation: a blurred scrim so the outgoing screen stays faintly visible.
 *
 * Presence is a `visible` flag with a CSS transition, not an AnimatePresence
 * mount/unmount. Two things went wrong when motion owned it: AnimatePresence
 * waits for every animation in the subtree before unmounting and the logo pulse
 * repeats forever, and once mounted the overlay animated its opacity up but
 * never back down. Either way the overlay stuck at full opacity over the app,
 * swallowing every tap. A CSS transition on an always-mounted element cannot
 * get stuck, and the global prefers-reduced-motion reset in globals.css already
 * collapses it to an instant cut.
 *
 * The pulse stays a JS animation, which those CSS resets do not reach, so
 * reduced motion is handled explicitly below.
 */
export function ScreenLoader({
  variant = 'transition',
  visible = true,
  progress,
}: {
  variant?: 'boot' | 'transition';
  visible?: boolean;
  /**
   * 0..1 for a determinate bar under the logo, omitted for none.
   *
   * Only pass this where the number is REAL — Discover derives it from how many
   * of its four sections have resolved. A timer pretending to be progress is
   * worse than no bar: it reads as informative and is not, and it always
   * finishes before the screen does.
   */
  progress?: number | null;
}) {
  const { reducedMotion } = useSettings();
  const isBoot = variant === 'boot';
  const [showReload, setShowReload] = useState(false);
  // Keeps a faded-out overlay off the compositor without cutting the fade short.
  const [faded, setFaded] = useState(!visible);

  // Every wait gets a count, and the count is never the caller's number.
  //
  // `progress` used to be rendered straight, so the one screen that supplied it
  // (Discover, from four sections resolving) drew 0 → 25 → 50 → 75 → 100 and
  // read as teleporting. It now steers the target while useProgressCounter
  // walks the displayed value toward it, and screens with nothing to report get
  // the same motion from the hook's own climb instead of no bar at all.
  //
  // `visible` going false is the finish signal: the count is pulled to 100
  // during the fade-out rather than being cut off wherever it had reached.
  const pct = useProgressCounter(visible, { realProgress: progress, reducedMotion });

  // The logo and the count outlive `visible` by exactly the fade.
  //
  // They used to be gated on `visible` alone, which unmounted them the instant
  // the wait ended and left an empty scrim fading on its own. That is also
  // where the finish would have been lost: the count is pulled to 100 during
  // those same milliseconds, so cutting it there would show the bar stopping
  // at whatever it happened to reach. Keeping both until `faded` lets the
  // count land, and keeps them fading together instead of the bar reflowing
  // upward the moment the logo goes.
  const showContent = visible || !faded;

  useEffect(() => {
    if (!isBoot || !visible) return;
    const timer = setTimeout(() => setShowReload(true), 15000);
    return () => clearTimeout(timer);
  }, [isBoot, visible]);

  useEffect(() => {
    if (visible) {
      setFaded(false);
      return;
    }
    const timer = setTimeout(() => setFaded(true), isBoot ? 800 : 250);
    return () => clearTimeout(timer);
  }, [visible, isBoot]);

  return (
    <div
      aria-hidden={!visible}
      style={{
        zIndex: isBoot ? 9999 : 100000,
        opacity: visible ? 1 : 0,
        transition: `opacity ${isBoot ? 800 : 250}ms ease-in-out`,
        pointerEvents: visible ? 'auto' : 'none',
        visibility: !visible && faded ? 'hidden' : 'visible',
      }}
      className={
        isBoot
          ? 'w-full h-screen bg-[#eddeb7] dark:bg-black flex flex-col items-center justify-center overflow-hidden fixed inset-0'
          : 'w-full h-screen bg-light-pattern/80 dark:bg-[#0D0D0D]/80 backdrop-blur-md flex flex-col items-center justify-center overflow-hidden fixed inset-0'
      }
    >
      <div className={`absolute inset-0 pointer-events-none ${isBoot ? 'opacity-30' : ''}`}>
        <ParallaxBackground speed={0.05} />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        {showContent && (
          <motion.img
            initial={{ scale: reducedMotion ? 1 : 0.9, opacity: 0 }}
            animate={{ scale: reducedMotion ? 1 : [1, 1.08, 1], opacity: 1 }}
            transition={{
              scale: reducedMotion
                ? { duration: 0 }
                : { duration: 2.6, repeat: Infinity, ease: 'easeInOut' },
              opacity: { duration: reducedMotion ? 0.2 : 0.8, ease: 'easeOut' },
            }}
            src={afroYapLogo as any}
            alt="AfroYap"
            className={isBoot ? 'w-32 h-auto drop-shadow-2xl' : 'w-24 h-auto'}
          />
        )}

        {/* The count, under the logo. Every wait shows one now, whether or not
            the screen can measure itself — see useProgressCounter for why a
            climb the user can follow beats a bar that only some screens get.

            No CSS width transition any more: the hook already publishes a new
            integer every frame, so animating between them would be a second,
            slower animation fighting the first and lagging the number beside
            it. Reduced motion is handled inside the hook, which snaps instead
            of climbing. */}
        {/* Spacing is inline rather than utility classes: index.css is committed
            pre-compiled Tailwind and contains neither mt-5 nor gap-1.5, so the
            intended spacing never applied and the bar sat flush under the logo. */}
        {showContent && (
          <div className="flex flex-col items-center" style={{ width: 132, marginTop: 34, gap: 6 }}>
            <div
              className="w-full rounded-full overflow-hidden"
              style={{ height: 3, backgroundColor: 'rgba(127,127,127,0.25)' }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label="Loading"
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  backgroundColor: '#4478ea',
                }}
              />
            </div>
            {/* fontVariantNumeric inline: tabular-nums is not in the compiled
                stylesheet either, and without it the percentage jitters as the
                digits change width. */}
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(127,127,127,0.85)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pct}%
            </span>
          </div>
        )}

        {showReload && visible && (
          <div className="mt-8 flex flex-col items-center gap-3">
            <p className="text-sm text-black/60 dark:text-white/60">
              This is taking longer than usual.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2 rounded-full bg-[#F39C12] dark:bg-[#4478ea] text-white text-sm font-semibold"
            >
              Reload
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
